import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import parse from 'html-react-parser';
import { FC, useState } from 'react';

import { SavedMessage } from '@features/savedMessages';
import { white } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import TemplateSavedMessageModal from './TemplateSavedMessageModal';
import TemplateDeleteSavedMessageModal from './TemplateDeleteSavedMessageModal';
import { useGetTemplateSavedMessages } from '../hooks/useGetTemplateSavedMessages';
import { EnterpriseTemplate } from '../types';
import { getCookie } from '@utils/getCookie';
import ConfirmationModal from './ConfirmationModal';

type TemplateSavedMessagesTabProps = {
  templateData: EnterpriseTemplate;
};

const TemplateSavedMessagesTab: FC<TemplateSavedMessagesTabProps> = ({
  templateData,
}) => {
  const dontShowWarningPopup = getCookie('dontShowWarningPopup');
  const isAppliedChanges = templateData.apply_changes && !dontShowWarningPopup;
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<SavedMessage | null>(null);
  const [isDeleteMessageModalOpen, setIsDeleteMessageModalOpen] =
    useState(false);
  const [checkDelete, setCheckDelete] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<SavedMessage | null>(
    null,
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const handleCancel = () => {
    setCheckDelete(false);
    setPendingUpdate(null);
    setIsConfirmOpen(false);
  };
  const handleConfirm = () => {
    if (pendingUpdate && !checkDelete) {
      setSelectedMessage(pendingUpdate);
      setIsMessageModalOpen(true);
    }
    if (pendingUpdate && checkDelete) {
      setSelectedMessage(pendingUpdate);
      setIsDeleteMessageModalOpen(true);
    }
    if (!pendingUpdate && !checkDelete) {
      setIsMessageModalOpen(true);
    }
    setIsConfirmOpen(false);
    setPendingUpdate(null);
    setCheckDelete(false);
  };
  const { savedMessages } = useGetTemplateSavedMessages({
    templateId: templateData?.id,
  });

  return (
    <Stack spacing={2}>
      <Button
        variant="contained"
        color="success"
        startIcon={<AddOutlinedIcon />}
        onClick={() => {
          if (isAppliedChanges) {
            setIsConfirmOpen(true);
          } else {
            setIsMessageModalOpen(true);
          }
        }}
        sx={{ textAlign: 'end', alignSelf: 'end', width: 'fit-content' }}
      >
        Add Message
      </Button>
      <Stack
        spacing={3}
        bgcolor={white}
        p={3}
        borderRadius={8}
        width="100%"
        alignItems="center"
      >
        {savedMessages &&
        savedMessages?.object &&
        savedMessages?.object?.length > 0 ? (
          savedMessages?.object?.map((message: SavedMessage) => (
            <Grid
              container
              key={message.id}
              alignItems="center"
              sx={{
                marginBottom: '10px !impotant',
                paddingBottom: '10px !important',
              }}
            >
              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle1" fontWeight={medium}>
                  {message.label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                {parse(message.content)}
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => {
                        if (isAppliedChanges) {
                          setIsConfirmOpen(true);
                          setPendingUpdate(message);
                        } else {
                          setSelectedMessage(message);
                          setIsMessageModalOpen(true);
                        }
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineOutlinedIcon />}
                      onClick={() => {
                        if (isAppliedChanges) {
                          setIsConfirmOpen(true);
                          setPendingUpdate(message);
                          setCheckDelete(true);
                        } else {
                          setSelectedMessage(message);
                          setIsDeleteMessageModalOpen(true);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ margin: '10px' }}>
                <Divider />
              </Grid>
            </Grid>
          ))
        ) : (
          <Box display="flex" justifyContent="center" textAlign="center">
            <Alert severity="info" sx={{ maxWidth: 'sm' }}>
              No Saved Messages here!
            </Alert>
          </Box>
        )}
        <TemplateSavedMessageModal
          isOpen={isMessageModalOpen}
          templateId={templateData?.id}
          handleClose={() => {
            setIsMessageModalOpen(false);
            setSelectedMessage(null);
          }}
          message={selectedMessage}
        />
        <TemplateDeleteSavedMessageModal
          isOpen={isDeleteMessageModalOpen}
          templateId={templateData?.id}
          handleClose={() => {
            setIsDeleteMessageModalOpen(false);
            setSelectedMessage(null);
          }}
          messageId={selectedMessage?.id as number}
        />
      </Stack>
      <ConfirmationModal
        open={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Stack>
  );
};

export default TemplateSavedMessagesTab;
